import cn from 'classnames';
import { useRouter } from 'next/dist/client/router';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { FC, LegacyRef, useCallback, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { SubmitHandler } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { useMediaQuery } from 'react-responsive';
import useSWR from 'swr';

import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';

import ThreeDSValidation, {
  type ThreeDSError,
  type ThreeDSValidationResponse,
} from '~components/Purchase/PurchaseFlow/ThreeDSValidation';
import SeatsPreview from '~components/Purchase/SeatsPreview';
import UserLoyalty from '~components/Purchase/UserLoyalty';
import OverflowContainer from '~components/common/OverflowContainer';
import CardThumbnail from '~components/ui/CardThumbnail';
import Checkbox from '~components/ui/Checkbox';
import Form from '~components/ui/Form';
import Input, { InputLocked } from '~components/ui/Input';
import inputStyles from '~components/ui/Input/Input.module.css';
import Notification from '~components/ui/Notification';
import { getFromAPI, postToAPI } from '~utils/api/common';
import useAuthUser from '~utils/hooks/useAuthUser';
import useGlobalState from '~utils/hooks/useGlobalState';
import useLocalStorage from '~utils/hooks/useLocalStorage';
import { formatPrice } from '~utils/hooks/usePrice';
import usePurchase from '~utils/hooks/usePurchase';
import useResponsive from '~utils/hooks/useResponsive';
import { normalizeTickets } from '~utils/normalizers';

import {
  Buy,
  BuyResponse,
  ErrorResponse,
  PaymentMethod,
  Purchase,
  SessionSeatsResponse,
  Show,
  Ticket,
} from '~types';

const DEFAULT_VALUES = {
  cardNumber: '',
  name: '',
  lastname: '',
  cardExp: '',
  cardCVV: '',
  email: '',
  phone: '',
  cardName: undefined,
  savePaymentMethod: false,
};

type FormTypes = {
  cardNumber: string;
  name: string;
  lastname: string;
  cardExp: string;
  cardCVV: string;
  email: string;
  phone: string;
  savePaymentMethod: boolean;
  cardName: string | undefined;
};

type ThreeDSState = {
  url: string;
  response: BuyResponse;
};

type BuyStatus = NonNullable<Buy['status']>;

type BuyStatusPayload = {
  status: string;
  id?: number;
  transIdTemp?: string;
  confirmationNumber?: string;
  conektaOrderId?: string;
  message?: string;
};

type BuyStatusSocketMessage = {
  event?: string;
  data?: unknown;
};

type BuyStatusWatch = {
  transIdTemp: string;
  response: BuyResponse;
  conektaOrderId?: string;
};

interface Props {
  showTickets: Ticket[];
  show: Show;
  visible: boolean;
}

const Tickets = dynamic(() => import('../Tickets/Tickets'), {
  ssr: false,
  loading: () => <span>Cargando boletos...</span>,
}) as typeof import('../Tickets/Tickets').default;

const DEFAULT_PAYMENT_ERROR_MESSAGE = 'Ocurrió un error en el servidor al procesar tu compra';
const DEFAULT_THREE_DS_ERROR_MESSAGE =
  'No fue posible confirmar la autenticación bancaria. Intenta completar la compra nuevamente.';
const THREE_DS_VALIDATION_TIMEOUT_MS = 45 * 1000;

const getErrorMessage = (
  error: Partial<ErrorResponse> | ThreeDSError | string | undefined,
  fallback = DEFAULT_THREE_DS_ERROR_MESSAGE,
) => {
  if (typeof error === 'string') return error;

  return error?.message || fallback;
};

const BUY_PENDING_STATUSES: BuyStatus[] = ['inProcess', '3ds'];
const BUY_SUCCESS_STATUSES: BuyStatus[] = ['paid', 'completed'];
const BUY_ERROR_STATUSES: BuyStatus[] = ['cancelled', 'error', 'invalid-3ds'];
const BUY_KNOWN_STATUSES: BuyStatus[] = [
  ...BUY_PENDING_STATUSES,
  ...BUY_SUCCESS_STATUSES,
  ...BUY_ERROR_STATUSES,
];

const isBuyStatus = (status: unknown): status is BuyStatus => {
  return BUY_KNOWN_STATUSES.includes(status as BuyStatus);
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === 'object';
};

const isBuyStatusPayload = (value: unknown): value is BuyStatusPayload => {
  return isRecord(value) && typeof value.status === 'string';
};

const parseBuyStatusSocketMessage = (data: MessageEvent['data']): BuyStatusPayload | null => {
  let message: unknown = data;

  if (typeof data === 'string') {
    try {
      message = JSON.parse(data);
    } catch {
      return null;
    }
  }

  if (isBuyStatusPayload(message)) return message;

  if (!isRecord(message)) return null;

  const socketMessage = message as BuyStatusSocketMessage;
  return isBuyStatusPayload(socketMessage.data) ? socketMessage.data : null;
};

const getBuyStatusFromSocket = (payload: BuyStatusPayload) => {
  return isBuyStatus(payload.status) ? payload.status : undefined;
};

const getSocketErrorMessage = (payload: BuyStatusPayload, status: BuyStatus) => {
  if (payload.message) return payload.message;

  if (status === 'cancelled') {
    return 'La compra fue cancelada durante la autenticación bancaria.';
  }

  if (status === 'invalid-3ds') {
    return 'No fue posible validar la autenticación 3DS.';
  }

  return DEFAULT_THREE_DS_ERROR_MESSAGE;
};

const getBuyWebSocketUrl = (transIdTemp: string) => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = new URL('/ws/buys', `${protocol}//${window.location.host}`);
  url.searchParams.set('transIdTemp', transIdTemp);

  return url.toString();
};

const Payment: FC<Props> = ({ showTickets, visible, show }) => {
  const recaptchaRef = useRef<ReCAPTCHA>();
  const purchaseFinishedRef = useRef(false);
  const threeDSValidationKeyRef = useRef<string | null>(null);
  const [, setOverlayProps] = useGlobalState('OverlayMessage');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const { isDark } = useResponsive();
  const isSmallestSize = useMediaQuery({ maxWidth: 359 });
  const [localBuys, setLocalBuys] = useLocalStorage<Buy[]>('Buys', []);
  const [threeDS, setThreeDS] = useState<ThreeDSState | null>(null);
  const [buyStatusWatch, setBuyStatusWatch] = useState<BuyStatusWatch | null>(null);
  const { query, replace, asPath } = useRouter();
  const user = useAuthUser();
  const { tickets, seats, transIdTemp, updatePurchase, total, bookingFee, successPurchase } =
    usePurchase();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | undefined>(undefined);
  const form = Form.useForm<FormTypes>({ defaultValues: DEFAULT_VALUES, mode: 'all' });
  const { errors, isValid } = form.formState;
  const savePaymentMethod = form.watch('savePaymentMethod');
  const { data: cards } = useSWR<PaymentMethod[]>(!!user ? '/payment-methods' : null, {
    revalidateOnFocus: false,
  });

  const { data: roomLayout } = useSWR<SessionSeatsResponse>(
    `/shows/seats/${query.session}`,
    (url: string) => getFromAPI(url, { notifyErrors: false }),
    {
      revalidateOnMount: false,
    },
  );

  useEffect(() => {
    if (!visible) {
      if (threeDS) setThreeDS(null);
      if (buyStatusWatch) setBuyStatusWatch(null);
    }
  }, [visible, threeDS, buyStatusWatch]);

  useEffect(() => {
    if (user) {
      const { email, phone, name, lastname } = user;
      const formValues = {
        ...DEFAULT_VALUES,
        name,
        lastname,
        email: email ?? '',
        phone: (paymentMethod ? paymentMethod.phone || phone : phone) ?? '',
        ...(paymentMethod
          ? { cardExp: `${paymentMethod?.expMonth}/${paymentMethod?.expYear}`.replaceAll('_', '') }
          : {}),
      };
      form.reset(formValues);
      if (paymentMethod) form.setFocus('cardCVV');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, paymentMethod]);

  useEffect(() => {
    if (savePaymentMethod) form.setFocus('cardName');
  }, [form, savePaymentMethod]);

  useEffect(() => {
    if (query.step === 'pago' && seats?.length === 0) {
      replace(asPath.replace('?step=pago', '?step=inicio'), undefined, { shallow: true }).then(
        () => {
          Notification.info({ title: 'El primer paso es seleccionar tus asientos.' });
        },
      );
    }
  }, [asPath, query, replace, seats?.length]);

  const saveCardField = useRef(false);
  saveCardField.current = form.watch('savePaymentMethod', false);

  const buySuccess = useCallback(
    (response: BuyResponse) => {
      purchaseFinishedRef.current = true;
      threeDSValidationKeyRef.current = null;
      setThreeDS(null);
      setBuyStatusWatch(null);
      if (response.buy?.confirmationNumber) {
        setOverlayProps({
          title: 'Compra exitosa.',
          message: 'Generando tu pase de entrada.',
          icon: <CheckCircleFilled className="text-3xl" />,
          type: 'success',
          visible: true,
        });
        // Nos aseguramos de quitar el transIdTemp para que no se cancele en el cambio de página
        updatePurchase({ transIdTemp: undefined });
        setLocalBuys([...localBuys, response.buy]);
        replace(`/compra/${response.buy.confirmationNumber}?gracias`).then(() => {
          successPurchase();
        });
      }
    },
    [localBuys, replace, successPurchase, setLocalBuys, setOverlayProps, updatePurchase],
  );

  const handleThreeDSValidationError = useCallback(
    (error: ThreeDSError, apiResponse: BuyResponse) => {
      purchaseFinishedRef.current = true;
      threeDSValidationKeyRef.current = null;
      setThreeDS(null);
      setBuyStatusWatch(null);
      setOverlayProps({
        title: 'Error al procesar el pago',
        message: getErrorMessage(error),
        icon: <CloseCircleFilled className="text-5xl" />,
        type: 'error',
        visible: true,
        children: (
          <button className="btn text-black" onClick={() => setOverlayProps({})}>
            Aceptar
          </button>
        ),
      });
      const newData: Partial<Purchase> = { processingPurchase: false };
      if (apiResponse.transIdTemp) {
        newData.transIdTemp = apiResponse.transIdTemp;
      }
      if (apiResponse?.buy?.seats) {
        newData.reservedSeats = apiResponse.buy.seats;
      }
      if (apiResponse.expDate) {
        newData.expDate = apiResponse.expDate;
      }
      updatePurchase(newData);
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    },
    [setOverlayProps, updatePurchase],
  );

  const confirmThreeDSBuy = useCallback(
    (apiResponse: BuyResponse, conektaOrderId: string) => {
      if (purchaseFinishedRef.current || !apiResponse.transIdTemp) return;

      const validationKey = `${apiResponse.transIdTemp}:${conektaOrderId}`;
      if (threeDSValidationKeyRef.current === validationKey) return;

      threeDSValidationKeyRef.current = validationKey;
      setThreeDS(null);
      setOverlayProps({
        title: 'Validando pago',
        message: 'Estamos confirmando la autenticación bancaria.',
        icon: <LoadingOutlined className="text-5xl" />,
        visible: true,
      });
      updatePurchase({ processingPurchase: true });

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => {
        controller.abort();
      }, THREE_DS_VALIDATION_TIMEOUT_MS);

      getFromAPI<BuyResponse>(
        `/buys/three-ds-validated/${encodeURIComponent(
          apiResponse.transIdTemp,
        )}/${encodeURIComponent(conektaOrderId)}`,
        { notifyErrors: false, signal: controller.signal },
      )
        .then((response) => {
          if (purchaseFinishedRef.current) return;

          if (response.buy?.confirmationNumber) {
            buySuccess(response);
            return;
          }

          handleThreeDSValidationError(
            { message: response.message || DEFAULT_THREE_DS_ERROR_MESSAGE },
            response,
          );
        })
        .catch((e: ErrorResponse) => {
          if (purchaseFinishedRef.current) return;

          handleThreeDSValidationError(
            {
              message:
                e?.statusCode === 500
                  ? DEFAULT_PAYMENT_ERROR_MESSAGE
                  : getErrorMessage(e, DEFAULT_PAYMENT_ERROR_MESSAGE),
            },
            apiResponse,
          );
        })
        .finally(() => {
          if (threeDSValidationKeyRef.current === validationKey) {
            threeDSValidationKeyRef.current = null;
          }
          window.clearTimeout(timeoutId);
        });
    },
    [buySuccess, handleThreeDSValidationError, setOverlayProps, updatePurchase],
  );

  const handleThreeDSValidationSuccess = useCallback(
    (threeDsData: ThreeDSValidationResponse) => {
      const apiResponse = threeDS?.response;
      setThreeDS(null);

      if (!apiResponse?.transIdTemp) {
        handleThreeDSValidationError(
          {
            message:
              'La autenticación 3DS fue aprobada, pero no encontramos la transacción temporal.',
          },
          apiResponse || ({} as BuyResponse),
        );
        return;
      }

      setBuyStatusWatch((current) => {
        if (!current || current.transIdTemp !== apiResponse.transIdTemp) return current;

        return { ...current, conektaOrderId: threeDsData.order.id };
      });
      confirmThreeDSBuy(apiResponse, threeDsData.order.id);
    },
    [confirmThreeDSBuy, handleThreeDSValidationError, threeDS?.response],
  );

  const handleBuyStatusSocketMessage = useCallback(
    (payload: BuyStatusPayload) => {
      if (!buyStatusWatch || purchaseFinishedRef.current) return;

      if (payload.transIdTemp && payload.transIdTemp !== buyStatusWatch.transIdTemp) return;

      const status = getBuyStatusFromSocket(payload);
      if (!status || BUY_PENDING_STATUSES.includes(status)) return;

      if (BUY_SUCCESS_STATUSES.includes(status)) {
        if (payload.confirmationNumber) {
          buySuccess({
            ...buyStatusWatch.response,
            confirmationNumber: payload.confirmationNumber,
            buy: {
              ...(buyStatusWatch.response.buy || {}),
              id: payload.id || buyStatusWatch.response.buy?.id,
              confirmationNumber: payload.confirmationNumber,
              status,
              transIdTemp: buyStatusWatch.transIdTemp,
            } as Buy,
          });
          return;
        }

        const conektaOrderId = payload.conektaOrderId || buyStatusWatch.conektaOrderId;
        if (conektaOrderId) {
          confirmThreeDSBuy(buyStatusWatch.response, conektaOrderId);
          return;
        }

        setOverlayProps({
          title: 'Compra aprobada',
          message: 'Estamos preparando tu pase de entrada.',
          icon: <LoadingOutlined className="text-5xl" />,
          visible: true,
        });
        updatePurchase({ processingPurchase: true });
        return;
      }

      handleThreeDSValidationError(
        { message: getSocketErrorMessage(payload, status) },
        buyStatusWatch.response,
      );
    },
    [
      buyStatusWatch,
      buySuccess,
      confirmThreeDSBuy,
      handleThreeDSValidationError,
      setOverlayProps,
      updatePurchase,
    ],
  );

  useEffect(() => {
    if (!visible || !buyStatusWatch?.transIdTemp || typeof window === 'undefined') return;

    const socket = new WebSocket(getBuyWebSocketUrl(buyStatusWatch.transIdTemp));
    socket.onmessage = (event) => {
      const payload = parseBuyStatusSocketMessage(event.data);
      console.log('socketMessage', payload);
      if (payload) handleBuyStatusSocketMessage(payload);
    };

    return () => {
      socket.close();
    };
  }, [buyStatusWatch?.transIdTemp, handleBuyStatusSocketMessage, visible]);

  const onSubmit: SubmitHandler<FormTypes> = (values) => {
    purchaseFinishedRef.current = false;
    threeDSValidationKeyRef.current = null;
    setBuyStatusWatch(null);

    const data = {
      ...values,
      PaymentMethodId: paymentMethod?.id || null,
      seats,
      tickets: normalizeTickets(tickets),
      sessionId: query.session,
      transIdTemp,
      bookingFee,
      captchaToken,
    };
    setOverlayProps({
      title: 'Procesando la compra',
      message: 'Espera un momento por favor.',
      icon: <LoadingOutlined className="text-5xl" />,
      visible: true,
    });
    updatePurchase({ processingPurchase: true });
    postToAPI<BuyResponse>('/buys', data, { notifyErrors: false })
      .then((response) => {
        if (response.threeDSValidationUrl) {
          if (!response.transIdTemp) {
            handleThreeDSValidationError(
              {
                message:
                  'No encontramos la transacción temporal para escuchar el estado de la compra.',
              },
              response,
            );
            return;
          }

          const newData: Partial<Purchase> = {
            processingPurchase: false,
          };
          if (response.expDate) newData.expDate = response.expDate;
          if (response.transIdTemp) newData.transIdTemp = response.transIdTemp;
          if (response?.buy?.seats) newData.reservedSeats = response.buy.seats;

          setOverlayProps({});
          setThreeDS({
            url: response.threeDSValidationUrl,
            response: response,
          });
          setBuyStatusWatch({
            transIdTemp: response.transIdTemp,
            response,
          });
          updatePurchase(newData);
        } else {
          buySuccess(response);
        }
      })
      .catch((e: ErrorResponse) => {
        recaptchaRef.current?.reset();
        setCaptchaToken(null);
        const overlayContent = (() => {
          switch (e?.data?.type) {
            case 'SEAT_SELECTION_FAIL':
              return (
                <Link
                  href={asPath.replace('?step=pago', '?step=inicio')}
                  shallow
                  className="btn text-neutral-800"
                  onClick={() => setOverlayProps({})}
                >
                  Seleccionar otros asientos
                </Link>
              );
            default:
              return (
                <button className="btn text-black" onClick={() => setOverlayProps({})}>
                  Aceptar
                </button>
              );
          }
        })();
        setOverlayProps({
          title: 'Error',
          message:
            e?.statusCode === 500
              ? DEFAULT_PAYMENT_ERROR_MESSAGE
              : getErrorMessage(e, DEFAULT_PAYMENT_ERROR_MESSAGE),
          icon: <CloseCircleFilled className="text-5xl" />,
          type: 'error',
          visible: true,
          children: overlayContent,
        });
        const newData: Partial<Purchase> = { processingPurchase: false };
        if (e?.data?.transIdTemp) {
          newData.transIdTemp = e?.data?.transIdTemp;
        }
        if (e?.data?.buy?.seats) {
          newData.reservedSeats = e?.data?.buy?.seats;
        }
        if (e?.data?.expDate) {
          newData.expDate = e?.data?.expDate;
        }
        updatePurchase(newData);
      });
  };

  const onRecaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const renderError = (field: keyof FormTypes) => {
    return errors[field]?.message ? (
      <small className="text-red-600">{String(errors[field]?.message)}</small>
    ) : null;
  };

  return (
    <section className={cn('container', { hidden: !visible })}>
      <div className="my-10">
        <div className="flex flex-row items-center space-x-6">
          <Link
            href={asPath.replace('?step=pago', '?step=inicio')}
            shallow
            className="btn translate-y-[-4px] dark:btn-dark"
          >
            <ArrowLeftOutlined />
          </Link>
          <h1 className="h2 sm:h1 mb-0">Detalles del pago</h1>
        </div>
        <p>
          Llena los datos de tu tarjeta de crédito o débito, compra tus entradas ahora y no hagas
          filas.
        </p>
      </div>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className=" grid grid-cols-12 gap-y-10 md:gap-x-10">
          <div className="col-span-12 lg:col-span-8">
            <div className="border border-dashed border-line lg:mr-12">
              <div className="bg-neutral-50 py-6 px-4 dark:bg-neutral-800 md:py-8 md:px-6">
                <UserLoyalty total={total} />
              </div>
              {cards && cards?.length > 0 && (
                <div className="border-b border-dashed border-line py-8 px-4 md:p-8">
                  <h3 className="h5 mb-4">Tus tarjetas guardadas:</h3>
                  <OverflowContainer itemsWithoutPadding>
                    {cards.map((item) => (
                      <div key={item.id} className="px-2">
                        <CardThumbnail
                          data={item}
                          onSelect={setPaymentMethod}
                          onDeselect={() => {
                            setPaymentMethod(undefined);
                          }}
                          selected={paymentMethod?.id === item.id}
                        />
                      </div>
                    ))}
                  </OverflowContainer>
                </div>
              )}
              <div className="py-8 px-4 md:p-8">
                <div className="grid grid-cols-12 gap-x-2 sm:gap-x-6 gap-y-6">
                  {!paymentMethod && (
                    <>
                      <div
                        className={`col-span-8 md:col-span-9 ${errors.cardNumber && 'field-error'}`}
                      >
                        <label htmlFor="cardNumber">Número de la tarjeta:</label>
                        <InputMask
                          autoFocus
                          mask="9999 9999 9999 9999"
                          maskPlaceholder=""
                          className={inputStyles.control}
                          {...form.register('cardNumber', {
                            required: 'El número de tarjeta es requerido',
                            pattern: {
                              value: /\d{4}\s\d{4}\s\d{4}\s\d{4}/,
                              message: 'El número de la tarjeta debe tener 16 dígitos',
                            },
                          })}
                        />
                        {renderError('cardNumber')}
                      </div>
                      <div className="col-span-4 self-center md:col-span-3">
                        <div className="mt-6 flex flex-row gap-1">
                          <Image
                            src="/mock/visa.png"
                            width={40}
                            height={24}
                            className="w-full max-w-[40px]"
                            alt="Imagen VISA"
                          />
                          <Image
                            src="/mock/mastercard.png"
                            width={40}
                            height={24}
                            className="w-full max-w-[40px]"
                            alt="Imagen Mastercard"
                          />
                          <Image
                            src="/mock/amex.png"
                            width={40}
                            height={24}
                            className="w-full max-w-[40px]"
                            alt="Imagen AMEX"
                          />
                        </div>
                      </div>
                      <div className={`col-span-12 md:col-span-6 ${errors.name && 'field-error'}`}>
                        <label htmlFor="name">Nombre:</label>
                        <Input {...form.register('name', { required: 'El nombre es requerido' })} />
                        {renderError('name')}
                      </div>
                      <div
                        className={`col-span-12 md:col-span-6 ${errors.lastname && 'field-error'}`}
                      >
                        <label htmlFor="lastname">Apellido:</label>
                        <Input
                          {...form.register('lastname', {
                            required: 'El apellido es requerido',
                          })}
                        />
                        {renderError('lastname')}
                      </div>
                      <div
                        className={`col-span-12 md:col-span-6 ${errors.cardExp && 'field-error'}`}
                      >
                        <label htmlFor="cardExp">Fecha de expiración:</label>
                        <InputMask
                          mask="99/99"
                          placeholder="MM/AA"
                          className={inputStyles.control}
                          {...form.register('cardExp', {
                            required: 'La fecha de expiración es requerida',
                          })}
                        />
                        {renderError('cardExp')}
                      </div>
                    </>
                  )}
                  <div className={`col-span-12 md:col-span-6 ${errors.cardCVV && 'field-error'}`}>
                    <label htmlFor="cardCVV">Código de seguridad:</label>
                    <Input
                      autoFocus
                      type="password"
                      maxLength={4}
                      {...form.register('cardCVV', { required: 'CVV requerido' })}
                    />
                    {renderError('cardCVV')}
                  </div>
                  <div className={`col-span-12 md:col-span-6 ${errors.email && 'field-error'}`}>
                    <label htmlFor="email">Correo electrónico:</label>
                    {user ? (
                      <InputLocked
                        type="email"
                        {...form.register('email', {
                          required: 'El correo electrónico es requerido',
                        })}
                      />
                    ) : (
                      <Input
                        type="email"
                        {...form.register('email', {
                          required: 'El correo electrónico es requerido',
                        })}
                      />
                    )}
                    {renderError('email')}
                  </div>
                  {!paymentMethod && (
                    <>
                      <div className={`col-span-12 md:col-span-6 ${errors.phone && 'field-error'}`}>
                        <label htmlFor="phone">Teléfono:</label>
                        <InputMask
                          mask="+52 999 999 9999"
                          placeholder="+52"
                          maskPlaceholder=""
                          className={inputStyles.control}
                          {...form.register('phone', {
                            pattern: {
                              value: /^\+52\s(\d{3})\s(\d{3})\s(\d{4})/,
                              message: 'Ingresa 10 dígitos',
                            },
                          })}
                        />
                        {renderError('phone')}
                      </div>
                      <div className="col-span-12 md:col-span-6 md:mt-6 md:self-center">
                        {!!user ? (
                          <Checkbox
                            id="guardaMetodoPago"
                            label="Guardar tarjeta"
                            disabled={paymentMethod !== undefined}
                            {...form.register('savePaymentMethod')}
                          />
                        ) : (
                          <span className="text-sm text-support">
                            Inicia sesión para que puedas almacenar esta tarjeta en tu cuenta
                            CitiPremia y la uses en tus próximas compras.
                          </span>
                        )}
                      </div>
                      {saveCardField.current && (
                        <div
                          className={`col-span-12 md:col-span-6 ${
                            errors?.cardName && 'field-error'
                          }`}
                        >
                          <label htmlFor="cardName">Agrega un nombre a tu tarjeta:</label>
                          <Input
                            {...form.register('cardName', {
                              required: savePaymentMethod ? 'Nombre de tarjeta requerido' : false,
                            })}
                          />
                          {renderError('cardName')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-4 col-span-12">
            <h2 className="h2">Asientos seleccionados</h2>
            {roomLayout && (
              <div className="hidden lg:block">
                <SeatsPreview data={roomLayout} roomType={show.roomType} />
                <div className="my-4 text-center">
                  <Link
                    href={asPath.replace('?step=pago', '?step=inicio')}
                    shallow
                    className="btn btn-sm dark:btn-dark"
                  >
                    <ArrowLeftOutlined />
                    <span>Cambiar asientos</span>
                  </Link>
                </div>
              </div>
            )}
            <p>Aquí puedes cambiar el tipo de boleto para cada asiento</p>
            {Tickets && <Tickets ticketsPrices={showTickets} locked={true} />}
          </div>
          <div className="col-span-12 space-y-4 md:order-2 md:col-span-4 flex flex-col items-center">
            <ReCAPTCHA
              ref={recaptchaRef as LegacyRef<ReCAPTCHA>}
              size={isSmallestSize ? 'compact' : 'normal'}
              theme={isDark ? 'dark' : 'light'}
              sitekey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY || 'saddd'}
              onChange={(token: string | null) => onRecaptchaChange(token)}
              onAbort={() => {
                setCaptchaToken(null);
              }}
              onExpired={() => {
                setCaptchaToken(null);
              }}
            />
            <button
              className="btn btn-primary btn-lg w-full"
              type="submit"
              disabled={
                process.env.NODE_ENV === 'production' ? !captchaToken || !isValid : !isValid
              }
            >
              Completar compra
            </button>
            <small className="block text-center">
              Al comprar acepto los{' '}
              <a href="/docs/terminos-y-condiciones.pdf" className="underline" target="_blank">
                Términos y condiciones
              </a>{' '}
              y el{' '}
              <a href="/aviso-privacidad" className="underline" target="_blank">
                Aviso de privacidad
              </a>{' '}
              de Citicinemas
            </small>
          </div>
          <ul className="col-span-12 list-disc space-y-2 md:order-1 md:col-span-8 mx-4">
            <li>
              <small>
                Los Datos Personales y Financieros que le son solicitados,serán tratados con las
                finalidades primarias de comprar y confirmar los boletos que adquiere.
              </small>
            </li>
            <li>
              <small>
                Los boletos son válidos únicamente para el día y horario seleccionado. No se
                realizarán reembolsos ni cambios de horarios una vez realizada la compra.
              </small>
            </li>
            {!!bookingFee ? (
              <li>
                <small>
                  Se realizará un cargo por servicio por cada boleto ({formatPrice(bookingFee || 0)}{' '}
                  MXN) en la compra
                </small>
              </li>
            ) : null}
          </ul>
        </div>
      </form>
      {!!threeDS && visible && (
        <ThreeDSValidation
          url={threeDS.url}
          apiResponse={threeDS.response}
          onSuccess={handleThreeDSValidationSuccess}
          onError={handleThreeDSValidationError}
        />
      )}
    </section>
  );
};

export default Payment;
